import { Button } from '@helix-ds/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { downloadExport, downloadExportArchive, triggerBulkExport } from '../api/fastenClient'
import { createPollCancelHandle, pollExportStatus } from '../pollExportStatus'
import type { EhiDownloadCounts, EhiExportStatus, StitchConnectionData } from '../types'
import FastenStitch from './FastenStitch'

type Action = 'trigger' | 'download'
type ConnectMode = 'direct' | 'tefca'

const PUBLIC_ID = import.meta.env.VITE_FASTEN_CONNECT_PUBLIC_ID ?? ''

const CONNECT_MODES: { id: ConnectMode; label: string }[] = [
  { id: 'direct', label: 'Direct' },
  { id: 'tefca', label: 'TEFCA IAS' },
]

const EXPORT_READY_MESSAGE =
  'You can click the Download button to save a zip of your EHR records and view resource counts below. In this POC, we are not storing your data anywhere. They will be cleared when you close this application.'

const FastenConnectCard: React.FC = () => {
  const client = useFamilyPortalApiClient()
  const [connectMode, setConnectMode] = useState<ConnectMode>('direct')
  const [connection, setConnection] = useState<StitchConnectionData | null>(null)
  const [status, setStatus] = useState<EhiExportStatus | null>(null)
  const [counts, setCounts] = useState<EhiDownloadCounts | null>(null)
  const [busy, setBusy] = useState<Action | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollCancelRef = useRef<ReturnType<typeof createPollCancelHandle> | null>(null)
  const exportEpochRef = useRef(0)

  const cancelPoll = useCallback(() => {
    pollCancelRef.current?.cancel()
    pollCancelRef.current = null
  }, [])

  const invalidateExportSession = useCallback(() => {
    exportEpochRef.current += 1
    cancelPoll()
  }, [cancelPoll])

  useEffect(() => () => invalidateExportSession(), [invalidateExportSession])

  const orgConnectionId = connection?.org_connection_id ?? null
  const hasDownloadLinks = (status?.downloadLinks?.length ?? 0) > 0
  const exportReady = hasDownloadLinks && (status?.status === 'success' || status?.status === 'completed')

  const handleConnected = useCallback((data: StitchConnectionData) => {
    setConnection(data)
    setError(null)
  }, [])

  const handleClosed = useCallback(() => {
    setError(null)
  }, [])

  const handleConnectModeChange = useCallback(
    (mode: ConnectMode) => {
      if (mode === connectMode) return
      invalidateExportSession()
      setConnectMode(mode)
      setConnection(null)
      setStatus(null)
      setCounts(null)
      setError(null)
      setBusy(null)
    },
    [connectMode, invalidateExportSession]
  )

  const run = useCallback(async (action: Action, fn: () => Promise<void>, epoch: number) => {
    setBusy(action)
    setError(null)
    try {
      await fn()
    } catch (err) {
      if (exportEpochRef.current === epoch) {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      }
    } finally {
      if (exportEpochRef.current === epoch) {
        setBusy(null)
      }
    }
  }, [])

  const onTrigger = () => {
    if (!orgConnectionId) return
    const epoch = ++exportEpochRef.current
    void run(
      'trigger',
      async () => {
        cancelPoll()
        const pollCancel = createPollCancelHandle()
        pollCancelRef.current = pollCancel
        await triggerBulkExport(client, orgConnectionId)
        if (exportEpochRef.current !== epoch) return

        setCounts(null)
        setStatus({ orgConnectionId, status: 'pending', downloadLinks: [] })
        await pollExportStatus(
          client,
          orgConnectionId,
          (next) => {
            if (exportEpochRef.current === epoch) {
              setStatus(next)
            }
          },
          { signal: pollCancel.signal }
        )
        if (pollCancelRef.current === pollCancel) {
          pollCancelRef.current = null
        }
      },
      epoch
    )
  }

  const onDownload = () => {
    if (!orgConnectionId) return
    const epoch = ++exportEpochRef.current
    void run(
      'download',
      async () => {
        const [archiveResult, countsResult] = await Promise.allSettled([
          downloadExportArchive(client, orgConnectionId),
          downloadExport(client, orgConnectionId),
        ])

        if (countsResult.status === 'fulfilled' && exportEpochRef.current === epoch) {
          setCounts(countsResult.value)
        }

        if (archiveResult.status === 'rejected') {
          const reason = archiveResult.reason
          throw reason instanceof Error ? reason : new Error('Failed to download export archive')
        }
        if (countsResult.status === 'rejected') {
          const reason = countsResult.reason
          throw reason instanceof Error ? reason : new Error('Failed to load resource counts')
        }
      },
      epoch
    )
  }

  const statusLabel = status?.status ?? (busy === 'trigger' ? 'pending' : null)

  return (
    <section className="border-border-base rounded-xl border bg-white px-6 py-6 shadow-lg shadow-gray-900/10 dark:bg-gray-900">
      <div className="flex flex-col gap-1">
        <p className="text-text-body text-sm font-medium tracking-wider uppercase dark:text-gray-400">
          Fasten Connect (POC)
        </p>
        <h2 className="text-text-heading text-2xl leading-8 font-semibold dark:text-white">
          Pull your records from your EHR
        </h2>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div>
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-text-body text-sm font-medium dark:text-gray-300">Connect to my EHR via Fasten</p>
            <div
              role="radiogroup"
              aria-label="Fasten Connect mode"
              className="border-border-base inline-flex rounded-full border p-0.5 dark:border-gray-700"
            >
              {CONNECT_MODES.map(({ id, label }) => {
                const selected = connectMode === id
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => handleConnectModeChange(id)}
                    className={`focus:ring-brand-600/20 cursor-pointer rounded-full px-3 py-1.5 text-sm font-medium transition-colors focus:ring-2 focus:outline-none ${
                      selected
                        ? 'bg-brand-600 dark:bg-brand-500 text-white'
                        : 'text-text-body hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          {connectMode === 'tefca' && (
            <p className="text-text-body mb-2 text-xs dark:text-gray-400">
              TEFCA IAS skips EHR search and uses identity verification (CLEAR or ID.me) instead of portal login.
            </p>
          )}
          {PUBLIC_ID ? (
            <FastenStitch
              key={connectMode}
              publicId={PUBLIC_ID}
              tefcaMode={connectMode === 'tefca'}
              onConnected={handleConnected}
              onClose={handleClosed}
            />
          ) : (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              Missing VITE_FASTEN_CONNECT_PUBLIC_ID — set it to enable the connect widget.
            </p>
          )}
          {orgConnectionId && (
            <p className="text-text-body mt-2 text-xs dark:text-gray-400">
              Connected ({connection?.platform_type ?? 'EHR'}) · connection id{' '}
              <code className="font-mono">{orgConnectionId}</code>
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            variant="primary"
            className="disabled:border-border-base cursor-pointer disabled:border dark:disabled:border-gray-700"
            onClick={onTrigger}
            disabled={!orgConnectionId || busy !== null}
          >
            {busy === 'trigger' ? 'Exporting…' : 'Trigger bulk export'}
          </Button>
          <Button
            variant={hasDownloadLinks ? 'primary' : 'secondary'}
            className={
              hasDownloadLinks
                ? 'disabled:border-border-base cursor-pointer disabled:border dark:disabled:border-gray-700'
                : 'cursor-pointer'
            }
            onClick={onDownload}
            disabled={!hasDownloadLinks || busy !== null}
          >
            {busy === 'download' ? 'Downloading…' : 'Download the EHI export'}
          </Button>
        </div>

        <div aria-live="polite" className="flex flex-col gap-3">
          {statusLabel && (
            <p className="text-text-body text-sm dark:text-gray-300">
              Export status: <span className="font-semibold">{statusLabel}</span>
              {hasDownloadLinks ? ` · ${status?.downloadLinks.length ?? 0} file(s) ready` : ''}
            </p>
          )}

          {exportReady && <p className="text-text-body text-sm dark:text-gray-300">{EXPORT_READY_MESSAGE}</p>}

          {counts && (
            <div className="border-border-base overflow-hidden rounded-lg border dark:border-gray-700">
              <p className="bg-gray-50 px-3 py-2 text-sm font-semibold dark:bg-gray-800 dark:text-gray-200">
                Downloaded {counts.totalResources} resource(s) across {counts.fileCount} file(s)
              </p>
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-text-body border-border-base border-b dark:border-gray-700 dark:text-gray-400">
                    <th scope="col" className="px-3 py-2 font-medium">
                      FHIR resource type
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      Count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(counts.resourceCounts).map(([type, count]) => (
                    <tr key={type} className="border-border-base border-b last:border-0 dark:border-gray-700">
                      <td className="px-3 py-2 dark:text-gray-200">{type}</td>
                      <td className="px-3 py-2 text-right tabular-nums dark:text-gray-200">{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </section>
  )
}

export default FastenConnectCard
